import { useState, useEffect } from "react";
import {
  useStripe,
  useElements,
  PaymentElement,
  Elements,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";

const BASE_URL = "https://adpayment-5ehl4nkpfa-uc.a.run.app";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

async function callFunction(path, uid, data) {
  const res = await fetch(`${BASE_URL}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...data, uid }),
  });
  return res.json();
}

// ─── Styles ──────────────────────────────────────────────────────────────────
const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.35)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: "1rem",
  },
  modal: {
    background: "#fff",
    borderRadius: "16px",
    width: "100%",
    maxWidth: "480px",
    maxHeight: "90vh",
    overflowY: "auto",
    padding: "1.75rem",
    fontFamily: "'DM Sans', sans-serif",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "1.5rem",
  },
  backBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "4px",
    display: "flex",
    alignItems: "center",
    color: "#888",
    borderRadius: "8px",
  },
  title: {
    fontSize: "18px",
    fontWeight: 600,
    color: "#1a1a1a",
    margin: 0,
  },
  summaryCard: {
    background: "#f7f5f0",
    borderRadius: "12px",
    padding: "1rem 1.25rem",
    marginBottom: "1.25rem",
  },
  summaryRow: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: "14px",
    padding: "4px 0",
    color: "#666",
  },
  summaryVal: {
    color: "#1a1a1a",
    fontWeight: 500,
  },
  divider: {
    border: "none",
    borderTop: "1px solid #e8e5df",
    margin: "8px 0",
  },
  totalRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "4px 0",
  },
  totalLabel: {
    fontSize: "15px",
    fontWeight: 600,
    color: "#1a1a1a",
  },
  totalAmount: {
    fontSize: "22px",
    fontWeight: 700,
    color: "#1a1a1a",
  },
  sectionLabel: {
    fontSize: "11px",
    fontWeight: 600,
    color: "#999",
    textTransform: "uppercase",
    letterSpacing: "0.07em",
    marginBottom: "0.6rem",
  },
  creditsBox: {
    background: "#fef3ee",
    border: "1px solid #f5c4b3",
    borderRadius: "10px",
    padding: "0.75rem 1rem",
    marginBottom: "1.25rem",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  creditsLeft: {
    fontSize: "13px",
    color: "#993C1D",
  },
  creditsBalance: {
    display: "block",
    fontSize: "16px",
    fontWeight: 600,
    color: "#712B13",
    marginBottom: "2px",
  },
  creditsApplied: {
    fontSize: "12px",
    color: "#C94E30",
    marginTop: "3px",
  },
  creditsRight: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "13px",
    color: "#993C1D",
  },
  methodCard: (selected) => ({
    border: selected ? "2px solid #E05A3A" : "1.5px solid #e8e5df",
    borderRadius: "12px",
    padding: "1rem 1.25rem",
    marginBottom: "0.75rem",
    cursor: "pointer",
    background: "#fff",
    transition: "border-color 0.15s",
  }),
  methodHeader: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  radio: (selected) => ({
    width: "18px",
    height: "18px",
    borderRadius: "50%",
    border: selected ? "5px solid #E05A3A" : "2px solid #ccc",
    flexShrink: 0,
    transition: "border 0.15s",
  }),
  methodName: {
    fontSize: "15px",
    fontWeight: 600,
    color: "#1a1a1a",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  methodDesc: {
    fontSize: "13px",
    color: "#888",
    marginTop: "2px",
  },
  badge: {
    fontSize: "11px",
    fontWeight: 600,
    background: "#fef3ee",
    color: "#993C1D",
    padding: "2px 8px",
    borderRadius: "20px",
  },
  invoiceInfo: {
    marginTop: "0.75rem",
    paddingTop: "0.75rem",
    borderTop: "1px solid #f0ede8",
    fontSize: "13px",
    color: "#666",
    lineHeight: 1.65,
  },
  stripeBadge: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "12px",
    color: "#999",
    marginBottom: "0.75rem",
  },
  stripeLogo: {
    fontWeight: 700,
    fontSize: "15px",
    color: "#635BFF",
    letterSpacing: "-0.5px",
  },
  payBtn: (disabled) => ({
    width: "100%",
    padding: "14px",
    borderRadius: "30px",
    border: "none",
    background: disabled ? "#e0ddd7" : "#E05A3A",
    color: disabled ? "#aaa" : "#fff",
    fontSize: "16px",
    fontWeight: 600,
    cursor: disabled ? "not-allowed" : "pointer",
    marginTop: "1.25rem",
    transition: "background 0.15s",
    fontFamily: "'DM Sans', sans-serif",
  }),
  secureNote: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "5px",
    fontSize: "12px",
    color: "#aaa",
    marginTop: "0.6rem",
  },
};

// ─── Toggle ───────────────────────────────────────────────────────────────────
function Toggle({ on, onToggle }) {
  return (
    <button
      onClick={onToggle}
      aria-label='Bruk kreditter'
      style={{
        width: "36px",
        height: "20px",
        borderRadius: "10px",
        background: on ? "#E05A3A" : "#f0ede8",
        border: "none",
        position: "relative",
        cursor: "pointer",
        transition: "background 0.2s",
        flexShrink: 0,
      }}>
      <div
        style={{
          width: "16px",
          height: "16px",
          borderRadius: "50%",
          background: "#fff",
          position: "absolute",
          top: "2px",
          left: on ? "18px" : "2px",
          transition: "left 0.2s",
          boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
        }}
      />
    </button>
  );
}

// ─── Stripe checkout form (shown after method selection) ──────────────────────
function CheckoutForm({ bookingData, onBack }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stripeReady, setStripeReady] = useState(false);

  const { days, startDate, endDate, pricePerDay, creditsApplied, amountDue } =
    bookingData;

  const handleSubmit = async () => {
    if (!stripe || !elements) return;
    setLoading(true);
    setError(null);

    const { error: stripeError } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/payment-success`,
      },
    });

    if (stripeError) {
      setError(stripeError.message);
      setLoading(false);
    }
  };

  return (
    <div style={styles.modal}>
      <div style={styles.header}>
        <button style={styles.backBtn} onClick={onBack} aria-label='Tilbake'>
          <svg
            width='20'
            height='20'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2.5'
            aria-hidden='true'>
            <path d='M19 12H5M12 5l-7 7 7 7' />
          </svg>
        </button>
        <h2 style={styles.title}>Betal for annonsering</h2>
      </div>

      <div style={styles.summaryCard}>
        <div style={styles.summaryRow}>
          <span>Periode</span>
          <span style={styles.summaryVal}>
            {startDate} – {endDate}
          </span>
        </div>
        <div style={styles.summaryRow}>
          <span>Antall dager</span>
          <span style={styles.summaryVal}>{days} dager</span>
        </div>
        <div style={styles.summaryRow}>
          <span>Pris per dag</span>
          <span style={styles.summaryVal}>
            kr {pricePerDay.toFixed(2).replace(".", ",")}
          </span>
        </div>
        {creditsApplied > 0 && (
          <div style={styles.summaryRow}>
            <span>Kreditter brukt</span>
            <span style={{ ...styles.summaryVal, color: "#E05A3A" }}>
              − kr {creditsApplied.toFixed(2).replace(".", ",")}
            </span>
          </div>
        )}
        <hr style={styles.divider} />
        <div style={styles.totalRow}>
          <span style={styles.totalLabel}>Totalt å betale</span>
          <span style={styles.totalAmount}>
            kr {amountDue.toFixed(2).replace(".", ",")}
          </span>
        </div>
      </div>

      <p style={styles.sectionLabel}>Kortbetaling</p>
      <div style={styles.stripeBadge}>
        <span style={styles.stripeLogo}>stripe</span>
        <span style={{ fontSize: "12px", color: "#999" }}>
          Sikker betaling via Stripe
        </span>
      </div>

      <PaymentElement onReady={() => setStripeReady(true)} />

      {error && (
        <p style={{ fontSize: "13px", color: "#E05A3A", marginTop: "0.75rem" }}>
          {error}
        </p>
      )}

      <button
        style={styles.payBtn(!stripe || !stripeReady || loading)}
        onClick={handleSubmit}
        disabled={!stripe || !stripeReady || loading}>
        {loading
          ? "Behandler..."
          : `Betal kr ${amountDue.toFixed(2).replace(".", ",")}`}
      </button>

      <div style={styles.secureNote}>
        <svg
          width='12'
          height='12'
          viewBox='0 0 24 24'
          fill='none'
          stroke='currentColor'
          strokeWidth='2.5'
          aria-hidden='true'>
          <rect x='3' y='11' width='18' height='11' rx='2' />
          <path d='M7 11V7a5 5 0 0 1 10 0v4' />
        </svg>
        Sikker og kryptert betaling
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function AdPaymentScreen({
  days = 5,
  startDate = "19.05.2026",
  endDate = "23.05.2026",
  uid = null,
  pricePerDay = 50,
  userCredits = 0,
  hasInvoice = false,
  eventName = null,
  eventId = null,
  startDateSeconds = null,
  onClose,
  onSuccess,
}) {
  const [method, setMethod] = useState("card");
  const [useCredits, setUseCredits] = useState(true);
  const [loading, setLoading] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [showStripeForm, setShowStripeForm] = useState(false);

  const subtotal = days * pricePerDay;
  const creditsApplied =
    useCredits && method === "card" ? Math.min(userCredits, subtotal) : 0;
  const amountDue = subtotal - creditsApplied;

  // Pre-fetch PaymentIntent as soon as card method is selected
  useEffect(() => {
    if (method !== "card" || clientSecret) return;
    callFunction("/createAdPaymentIntent", uid, {
      days,
      creditsToUse: creditsApplied * 100,
      eventId,
      eventName,
      startDateSeconds,
    })
      .then((result) => {
        if (result && !result.fulfilledWithCredits)
          setClientSecret(result.clientSecret);
      })
      .catch(console.error);
  }, [method]);

  const handleContinue = async () => {
    setLoading(true);

    try {
      if (method === "invoice") {
        await callFunction("/bookAdDaysOnInvoice", uid, {
          days,
          eventId,
          eventName,
          startDateSeconds,
        });
        onSuccess?.({ method: "invoice" });
        return;
      }

      if (amountDue === 0) {
        onSuccess?.({ method: "credits" });
        return;
      }

      setShowStripeForm(true);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // ── Stripe form screen ──────────────────────────────────────────────────────
  if (showStripeForm && clientSecret) {
    return (
      <div
        style={styles.overlay}
        onClick={(e) => e.target === e.currentTarget && onClose?.()}>
        <Elements
          stripe={stripePromise}
          options={{ clientSecret, locale: "nb" }}>
          <CheckoutForm
            bookingData={{
              days,
              startDate,
              endDate,
              pricePerDay,
              creditsApplied,
              amountDue,
            }}
            onBack={() => setShowStripeForm(false)}
          />
        </Elements>
      </div>
    );
  }

  // ── Method selection screen ─────────────────────────────────────────────────
  return (
    <div
      style={styles.overlay}
      onClick={(e) => e.target === e.currentTarget && onClose?.()}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <button style={styles.backBtn} onClick={onClose} aria-label='Tilbake'>
            <svg
              width='20'
              height='20'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2.5'
              aria-hidden='true'>
              <path d='M19 12H5M12 5l-7 7 7 7' />
            </svg>
          </button>
          <h2 style={styles.title}>Betal for annonsering</h2>
        </div>

        {/* Summary */}
        <div style={styles.summaryCard}>
          <div style={styles.summaryRow}>
            <span>Periode</span>
            <span style={styles.summaryVal}>
              {startDate} – {endDate}
            </span>
          </div>
          <div style={styles.summaryRow}>
            <span>Antall dager</span>
            <span style={styles.summaryVal}>{days} dager</span>
          </div>
          <div style={styles.summaryRow}>
            <span>Pris per dag</span>
            <span style={styles.summaryVal}>
              kr {pricePerDay.toFixed(2).replace(".", ",")}
            </span>
          </div>
          <hr style={styles.divider} />
          <div style={styles.totalRow}>
            <span style={styles.totalLabel}>Totalt</span>
            <span style={styles.totalAmount}>
              kr {subtotal.toFixed(2).replace(".", ",")}
            </span>
          </div>
        </div>

        {/* Credits */}
        {userCredits > 0 && method === "card" && (
          <div style={styles.creditsBox}>
            <div style={styles.creditsLeft}>
              <span style={styles.creditsBalance}>
                kr{" "}
                {userCredits.toLocaleString("nb-NO", {
                  minimumFractionDigits: 2,
                })}
              </span>
              tilgjengelige kreditter
              {creditsApplied > 0 && (
                <div style={styles.creditsApplied}>
                  kr {creditsApplied.toFixed(2).replace(".", ",")} trekkes fra
                  saldo
                </div>
              )}
            </div>
            <div style={styles.creditsRight}>
              <span>Bruk kreditter</span>
              <Toggle
                on={useCredits}
                onToggle={() => setUseCredits((v) => !v)}
              />
            </div>
          </div>
        )}

        {/* Split summary (credits + card) */}
        {creditsApplied > 0 && amountDue > 0 && (
          <div
            style={{
              ...styles.summaryCard,
              marginBottom: "1.25rem",
              background: "#fef3ee",
            }}>
            <div style={styles.summaryRow}>
              <span>Subtotal</span>
              <span style={styles.summaryVal}>
                kr {subtotal.toFixed(2).replace(".", ",")}
              </span>
            </div>
            <div style={styles.summaryRow}>
              <span>Kreditter</span>
              <span style={{ ...styles.summaryVal, color: "#E05A3A" }}>
                − kr {creditsApplied.toFixed(2).replace(".", ",")}
              </span>
            </div>
            <hr style={styles.divider} />
            <div style={styles.totalRow}>
              <span style={styles.totalLabel}>Å betale med kort</span>
              <span style={{ ...styles.totalAmount, fontSize: "18px" }}>
                kr {amountDue.toFixed(2).replace(".", ",")}
              </span>
            </div>
          </div>
        )}

        {/* Payment method */}
        <p style={styles.sectionLabel}>Betalingsmetode</p>

        <div
          style={styles.methodCard(method === "card")}
          onClick={() => setMethod("card")}>
          <div style={styles.methodHeader}>
            <div style={styles.radio(method === "card")} />
            <div style={{ flex: 1 }}>
              <div style={styles.methodName}>
                <svg
                  width='18'
                  height='18'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                  aria-hidden='true'>
                  <rect x='1' y='4' width='22' height='16' rx='2' />
                  <line x1='1' y1='10' x2='23' y2='10' />
                </svg>
                Kortbetaling
              </div>
              <div style={styles.methodDesc}>Betal umiddelbart med kort</div>
            </div>
          </div>
        </div>

        {hasInvoice && (
          <div
            style={styles.methodCard(method === "invoice")}
            onClick={() => setMethod("invoice")}>
            <div style={styles.methodHeader}>
              <div style={styles.radio(method === "invoice")} />
              <div style={{ flex: 1 }}>
                <div style={styles.methodName}>
                  <svg
                    width='18'
                    height='18'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='2'
                    aria-hidden='true'>
                    <path d='M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z' />
                    <polyline points='14 2 14 8 20 8' />
                    <line x1='16' y1='13' x2='8' y2='13' />
                    <line x1='16' y1='17' x2='8' y2='17' />
                  </svg>
                  Månedlig faktura
                  <span style={styles.badge}>Bedrift</span>
                </div>
                <div style={styles.methodDesc}>
                  Legg til på månedlig faktura
                </div>
              </div>
            </div>
            {method === "invoice" && (
              <div style={styles.invoiceInfo}>
                Annonsen aktiveres umiddelbart. Denne bestillingen legges til
                din månedlige faktura som sendes på e-post ved månedsslutt med{" "}
                <strong style={{ color: "#1a1a1a" }}>
                  14 dagers betalingsfrist
                </strong>
                .<br />
                <br />
                Fakturaen vil inneholde en spesifisert oversikt over alle
                annonsedager bestilt denne måneden.
              </div>
            )}
          </div>
        )}

        <button
          style={styles.payBtn(loading)}
          onClick={handleContinue}
          disabled={loading}>
          {loading
            ? "Behandler..."
            : method === "invoice"
              ? "Legg til på faktura"
              : amountDue === 0
                ? "Bekreft (dekkes av kreditter)"
                : `Gå videre · kr ${amountDue.toFixed(2).replace(".", ",")}`}
        </button>

        <div style={styles.secureNote}>
          {method === "invoice" ? (
            <>
              <svg
                width='12'
                height='12'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2.5'
                aria-hidden='true'>
                <rect x='1' y='4' width='22' height='16' rx='2' />
                <line x1='1' y1='10' x2='23' y2='10' />
              </svg>
              Faktureres ved månedsslutt
            </>
          ) : (
            <>
              <svg
                width='12'
                height='12'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2.5'
                aria-hidden='true'>
                <rect x='3' y='11' width='18' height='11' rx='2' />
                <path d='M7 11V7a5 5 0 0 1 10 0v4' />
              </svg>
              Sikker og kryptert betaling
            </>
          )}
        </div>
      </div>
    </div>
  );
}
